import { useRef, useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useAppSelector } from '../../hooks/useAppDispatch'
import { api } from '../../lib/apiClient'
import { ENDPOINTS } from '../../lib/endpoints'
import { getInitials, getAvatarColor } from '../../lib/avatar'
import { MENTION_DEBOUNCE_MS, MENTION_DROPDOWN_WIDTH, DROPDOWN_VIEWPORT_MARGIN } from '../../lib/config'

interface MentionUser {
  id: string
  displayName: string
  avatarUrl: string | null
  inDiscussion?: boolean
  isAgent?: boolean
}

interface MentionTextareaProps {
  value: string
  onChange: (value: string) => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void
  placeholder?: string
  rows?: number
  autoFocus?: boolean
  discussionId?: string       // used for smart suggestions
  allowAgentMentions?: boolean // only true in discussion context
}

export default function MentionTextarea({
  value,
  onChange,
  onKeyDown,
  placeholder = 'Write a reply...',
  rows = 2,
  autoFocus = false,
  discussionId,
  allowAgentMentions = false,
}: MentionTextareaProps) {
  const accessToken = useAppSelector((s) => s.auth.accessToken)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Mention state
  const [mentionQuery, setMentionQuery] = useState<string | null>(null) // null = closed
  const [mentionStart, setMentionStart] = useState(0)
  const [results, setResults] = useState<MentionUser[]>([])
  const [activeIdx, setActiveIdx] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [dropPos, setDropPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 })
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const activeItemRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (autoFocus) textareaRef.current?.focus()
  }, [autoFocus])

  // Fetch suggestions whenever mentionQuery changes
  useEffect(() => {
    if (mentionQuery === null) { setResults([]); return }
    if (debounceRef.current) clearTimeout(debounceRef.current)

    const delay = mentionQuery.length === 0 ? 0 : MENTION_DEBOUNCE_MS
    debounceRef.current = setTimeout(async () => {
      setIsLoading(true)
      try {
        const params = new URLSearchParams({ q: mentionQuery })
        if (discussionId) params.set('discussionId', discussionId)
        if (allowAgentMentions) params.set('includeAgents', 'true')
        const data = await api.get<{ users: MentionUser[] }>(
          'users',
          `${ENDPOINTS.users.mentionSuggest}?${params}`,
          accessToken ?? undefined
        )
        setResults(data.users)
        setActiveIdx(0)
      } catch {
        setResults([])
      } finally {
        setIsLoading(false)
      }
    }, delay)
  }, [mentionQuery, discussionId, accessToken])

  const positionDropdown = useCallback(() => {
    if (!textareaRef.current) return
    const rect = textareaRef.current.getBoundingClientRect()
    const DROPDOWN_WIDTH = MENTION_DROPDOWN_WIDTH
    const DROPDOWN_MAX_HEIGHT = 240
    let left = rect.left
    if (left + DROPDOWN_WIDTH > window.innerWidth - DROPDOWN_VIEWPORT_MARGIN)
      left = window.innerWidth - DROPDOWN_WIDTH - DROPDOWN_VIEWPORT_MARGIN
    // Flip upward if not enough space below
    const spaceBelow = window.innerHeight - rect.bottom
    const top = spaceBelow < DROPDOWN_MAX_HEIGHT + 8
      ? rect.top - DROPDOWN_MAX_HEIGHT - 4
      : rect.bottom + 4
    setDropPos({ top, left })
  }, [])

  const closeMention = useCallback(() => {
    setMentionQuery(null)
    setResults([])
    setActiveIdx(0)
    setIsLoading(false)
  }, [])

  const insertMention = useCallback((user: MentionUser) => {
    const before = value.slice(0, mentionStart)
    const after = value.slice(textareaRef.current?.selectionEnd ?? mentionStart + (mentionQuery?.length ?? 0) + 1)
    const inserted = `@${user.displayName} `
    onChange(before + inserted + after)
    closeMention()
    requestAnimationFrame(() => {
      const pos = before.length + inserted.length
      textareaRef.current?.setSelectionRange(pos, pos)
      textareaRef.current?.focus()
    })
  }, [value, mentionStart, mentionQuery, onChange, closeMention])

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value
    const cursor = e.target.selectionStart ?? 0
    onChange(text)
    const textToCursor = text.slice(0, cursor)
    const atMatch = textToCursor.match(/@([\w ]*)$/)
    if (atMatch && !atMatch[1].includes('  ')) {
      const atIndex = cursor - atMatch[0].length
      setMentionStart(atIndex)
      setMentionQuery(atMatch[1] ?? '')
      positionDropdown()
    } else {
      closeMention()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (mentionQuery !== null && results.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => (i + 1) % results.length); return }
      if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx((i) => (i - 1 + results.length) % results.length); return }
      if (e.key === 'Enter' || e.key === 'Tab') {
        const user = results[activeIdx]
        if (user) { e.preventDefault(); insertMention(user); return }
      }
      if (e.key === 'Escape') { e.preventDefault(); closeMention(); return }
    }
    onKeyDown?.(e)
  }

  // Scroll active item into view when navigating with keyboard
  useEffect(() => {
    if (activeItemRef.current && dropdownRef.current) {
      activeItemRef.current.scrollIntoView({ block: 'nearest' })
    }
  }, [activeIdx])

  // Close on outside click — but NOT on scroll inside the dropdown
  useEffect(() => {
    if (mentionQuery === null) return
    const onDown = (e: MouseEvent) => {
      if (
        !textareaRef.current?.contains(e.target as Node) &&
        !dropdownRef.current?.contains(e.target as Node)
      ) closeMention()
    }
    const onScroll = (e: Event) => {
      // Ignore scroll events originating inside the dropdown itself
      if (dropdownRef.current?.contains(e.target as Node)) return
      closeMention()
    }
    document.addEventListener('mousedown', onDown)
    window.addEventListener('scroll', onScroll, true)
    return () => {
      document.removeEventListener('mousedown', onDown)
      window.removeEventListener('scroll', onScroll, true)
    }
  }, [mentionQuery, closeMention])

  // Split results into groups
  const agents = results.filter((u) => u.isAgent)
  const inDiscussion = results.filter((u) => u.inDiscussion && !u.isAgent)
  const others = results.filter((u) => !u.inDiscussion && !u.isAgent)
  const showDropdown = mentionQuery !== null && (isLoading || results.length > 0)

  // Flat index mapping for keyboard nav across all sections
  const allResults = [...agents, ...inDiscussion, ...others]

  return (
    <>
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        rows={rows}
        style={{
          width: '100%',
          background: 'transparent',
          border: 'none',
          outline: 'none',
          resize: 'none',
          color: 'var(--text-primary)',
          fontSize: '0.825rem',
          fontFamily: 'inherit',
          lineHeight: 1.5,
          boxSizing: 'border-box',
        }}
      />

      {showDropdown && createPortal(
        <div
          ref={dropdownRef}
          className="mention-drop"
          style={{
            position: 'fixed',
            top: dropPos.top,
            left: dropPos.left,
            zIndex: 99999,
            width: `${MENTION_DROPDOWN_WIDTH}px`,
            maxHeight: '260px',
            overflowY: 'auto',
            overflowX: 'hidden',
            background: 'var(--bg-card)',
            border: '1px solid var(--border)',
            borderRadius: '10px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.22)',
            scrollbarWidth: 'thin',
            scrollbarColor: 'var(--border) transparent',
            animation: 'mentionFadeIn 0.1s ease',
          }}
        >
          <style>{`@keyframes mentionFadeIn { from { opacity:0; transform:translateY(-3px) } to { opacity:1; transform:translateY(0) } } .mention-drop::-webkit-scrollbar { width: 4px } .mention-drop::-webkit-scrollbar-thumb { background: var(--border); border-radius: 4px }`}</style>

          {isLoading && results.length === 0 ? (
            <div style={{ padding: '0.65rem 0.75rem', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
              Finding...
            </div>
          ) : (
            <>
              {/* AI Agents — discussions only */}
              {agents.length > 0 && (
                <>
                  <SectionLabel label="AI Agents" />
                  {agents.map((agent) => {
                    const flatIdx = allResults.indexOf(agent)
                    return (
                      <AgentRow
                        key={agent.id}
                        agent={agent}
                        active={flatIdx === activeIdx}
                        activeRef={flatIdx === activeIdx ? activeItemRef : undefined}
                        onSelect={() => insertMention(agent)}
                        onHover={() => setActiveIdx(flatIdx)}
                        query={mentionQuery ?? ''}
                      />
                    )
                  })}
                </>
              )}

              {/* In this discussion */}
              {inDiscussion.length > 0 && (
                <>
                  <SectionLabel label="In this discussion" showBorder={agents.length > 0} />
                  {inDiscussion.map((user) => {
                    const flatIdx = allResults.indexOf(user)
                    return (
                      <UserRow
                        key={user.id}
                        user={user}
                        active={flatIdx === activeIdx}
                        activeRef={flatIdx === activeIdx ? activeItemRef : undefined}
                        onSelect={() => insertMention(user)}
                        onHover={() => setActiveIdx(flatIdx)}
                        query={mentionQuery ?? ''}
                      />
                    )
                  })}
                </>
              )}

              {/* Other users */}
              {others.length > 0 && (
                <>
                  <SectionLabel label={inDiscussion.length > 0 ? 'Other users' : 'Suggested users'} showBorder={agents.length > 0 || inDiscussion.length > 0} />
                  {others.map((user) => {
                    const flatIdx = allResults.indexOf(user)
                    return (
                      <UserRow
                        key={user.id}
                        user={user}
                        active={flatIdx === activeIdx}
                        activeRef={flatIdx === activeIdx ? activeItemRef : undefined}
                        onSelect={() => insertMention(user)}
                        onHover={() => setActiveIdx(flatIdx)}
                        query={mentionQuery ?? ''}
                      />
                    )
                  })}
                </>
              )}

              {!isLoading && results.length === 0 && mentionQuery && mentionQuery.length > 0 && (
                <div style={{ padding: '0.65rem 0.75rem', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                  No results for "{mentionQuery}"
                </div>
              )}
            </>
          )}
        </div>,
        document.body
      )}
    </>
  )
}

function SectionLabel({ label, showBorder = false }: { label: string; showBorder?: boolean }) {
  return (
    <div style={{
      padding: '0.3rem 0.65rem',
      fontSize: '0.62rem',
      fontWeight: 700,
      color: 'var(--text-muted)',
      textTransform: 'uppercase',
      letterSpacing: '0.07em',
      borderTop: showBorder ? '1px solid var(--border)' : undefined,
      marginTop: showBorder ? '0.2rem' : undefined,
      paddingTop: showBorder ? '0.4rem' : undefined,
    }}>
      {label}
    </div>
  )
}

function AgentRow({ agent, active, activeRef, onSelect, onHover, query }: {
  agent: MentionUser
  active: boolean
  activeRef?: React.RefObject<HTMLDivElement>
  onSelect: () => void
  onHover: () => void
  query: string
}) {
  const highlight = (name: string, q: string) => {
    if (!q) return <span>{name}</span>
    const idx = name.toLowerCase().indexOf(q.toLowerCase())
    if (idx === -1) return <span>{name}</span>
    return (
      <span>
        {name.slice(0, idx)}
        <span style={{ color: 'var(--accent-primary)', fontWeight: 600 }}>{name.slice(idx, idx + q.length)}</span>
        {name.slice(idx + q.length)}
      </span>
    )
  }

  return (
    <div
      ref={activeRef}
      onMouseDown={(e) => { e.preventDefault(); onSelect() }}
      onMouseEnter={onHover}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.6rem',
        padding: '0.45rem 0.75rem',
        cursor: 'pointer',
        background: active ? 'var(--bg-elevated)' : 'transparent',
        transition: 'background 0.08s',
      }}
    >
      <div style={{
        width: '28px', height: '28px', borderRadius: '8px', flexShrink: 0,
        background: 'rgba(124,58,237,0.12)',
        border: '1px solid rgba(124,58,237,0.25)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '14px',
      }}>
        🤖
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '0.82rem', fontWeight: 500, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {highlight(agent.displayName, query)}
        </div>
        <div style={{ fontSize: '0.66rem', color: 'var(--accent-primary)', marginTop: '1px' }}>AI Agent</div>
      </div>
      <span style={{ fontSize: '0.55rem', fontWeight: 700, background: 'var(--accent-primary)', color: 'white', padding: '0.05rem 0.3rem', borderRadius: '3px', textTransform: 'uppercase', letterSpacing: '0.05em', flexShrink: 0 }}>AI</span>
    </div>
  )
}

function UserRow({ user, active, activeRef, onSelect, onHover, query }: {
  user: MentionUser
  active: boolean
  activeRef?: React.RefObject<HTMLDivElement>
  onSelect: () => void
  onHover: () => void
  query: string
}) {
  const [imgFailed, setImgFailed] = useState(false)

  const highlight = (name: string, q: string) => {
    if (!q) return <span>{name}</span>
    const idx = name.toLowerCase().indexOf(q.toLowerCase())
    if (idx === -1) return <span>{name}</span>
    return (
      <span>
        {name.slice(0, idx)}
        <span style={{ color: 'var(--accent-primary)', fontWeight: 600 }}>{name.slice(idx, idx + q.length)}</span>
        {name.slice(idx + q.length)}
      </span>
    )
  }

  const showImg = !!user.avatarUrl && !imgFailed

  return (
    <div
      ref={activeRef}
      onMouseDown={(e) => { e.preventDefault(); onSelect() }}
      onMouseEnter={onHover}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.6rem',
        padding: '0.45rem 0.75rem',
        cursor: 'pointer',
        background: active ? 'var(--bg-elevated)' : 'transparent',
        transition: 'background 0.08s',
      }}
    >
      {showImg ? (
        <img
          src={user.avatarUrl!}
          alt={user.displayName}
          style={{ width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0, objectFit: 'cover' }}
          onError={() => setImgFailed(true)}
        />
      ) : (
        <div style={{
          width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0,
          background: getAvatarColor(user.displayName),
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'white', fontWeight: 700, fontSize: '10px',
        }}>
          {getInitials(user.displayName)}
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '0.82rem', fontWeight: 500, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {highlight(user.displayName, query)}
        </div>
        {user.inDiscussion && (
          <div style={{ fontSize: '0.66rem', color: 'var(--accent-primary)', marginTop: '1px' }}>
            in this discussion
          </div>
        )}
      </div>
    </div>
  )
}
